import * as Api from '@ViewModels';
import React, { useEffect, useMemo } from 'react';
import { useStreamingResponseMutation } from '../../../../hooks/useStreamingResponseMutation';
import { IContentCalendarSelectedSuggestion } from '../../../../models';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getUniqueIdForSuggestion } from '../../../../models/UiUtils';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import { AdminUserSessionContext } from '../../../../viewmodels/AdminViewModels';

type ChatRequestTypeAction = {
	_type: 'ContentGenerationAction';
	contentType: 'email' | 'social' | 'blogTemplate';
} & ({ contentId: string } | { content: any });

type ChatRequestType = {
	_type: 'AIActionRequest';
	action: ChatRequestTypeAction;
};

export const useHandleAiGeneration = <TToolCall extends Api.EmailToolCall | Api.SocialMediaToolCall>({
	onAiGenerationComplete,
	impersonationContext,
	contentType,
	customContent,
}: {
	onAiGenerationComplete: (suggestion: IContentCalendarSelectedSuggestion, toolCall: TToolCall) => void;
	impersonationContext: Api.IImpersonationContext;
	contentType: 'email' | 'social' | 'blogTemplate';
	customContent?: Record<string, { subject: string; content: Api.IRawRichTextContentState }>;
}) => {
	const userSession = useUserSession<AdminUserSessionContext>();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useHandleAiGeneration');
	const [generatedContent, setGeneratedContent] = React.useState<{
		suggestion: IContentCalendarSelectedSuggestion;
		toolCall: TToolCall;
	} | null>(null);

	const [pendingItems, setPendingItems] = React.useState<IContentCalendarSelectedSuggestion[]>([]);
	const [startProcessing, setStartProcessing] = React.useState(false);
	const isGenerating = useMemo(() => pendingItems.length > 0, [pendingItems]);

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('useHandleAiGeneration', error);
		},
	});

	const [suggestionsWithErrors, setSuggestionsWithErrors] = React.useState<IContentCalendarSelectedSuggestion[]>([]);

	const onError = (error: Error, suggestion: IContentCalendarSelectedSuggestion) => {
		errorMessages.pushApiError(error);
		setSuggestionsWithErrors(prev => [...prev, suggestion]);
		removeItemFromQueue(pendingItems[0]);

		logger.logApiError('useHandleAiGeneration', error as Api.IOperationResultNoValue);
	};

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName,
			conversationId,
			chatRequest,
		}: {
			assistantName: string;
			conversationId: string;
			suggestion: IContentCalendarSelectedSuggestion;
			chatRequest: ChatRequestType;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${conversationId}/stream`,
						impersonationContext,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event, params) => {
			const data = JSON.parse(event.data);
			if (event.event === 'Error') {
				onError(new Error(event.data), params.suggestion);
				return;
			}

			if (event.event !== 'ToolResponse') {
				return;
			}

			const toolCall = data?.toolCall;

			if (
				toolCall?.body ||
				(toolCall?.toolName === 'lev-social-media-post' && toolCall?.content) ||
				(toolCall?.toolName === 'lev-blog' && toolCall?.content)
			) {
				setGeneratedContent({
					suggestion: params.suggestion,
					toolCall,
				});
			}
		},
		onSuccess: params => {
			if (generatedContent) {
				handleAiGenerationComplete(generatedContent.suggestion, generatedContent.toolCall);
				setGeneratedContent(null);
			} else {
				setSuggestionsWithErrors(prev => [...prev, params.suggestion]);
				removeItemFromQueue(params.suggestion);
			}
		},
		onError: (error, params) => {
			onError(error, params.suggestion);
		},
	});

	const removeItemFromQueue = (suggestion: IContentCalendarSelectedSuggestion) => {
		setPendingItems(prev =>
			prev.filter(item => item.templateReference.templateId !== suggestion.templateReference.templateId)
		);
	};

	const queueItemForAiGeneration = (suggestion: IContentCalendarSelectedSuggestion) => {
		setSuggestionsWithErrors(prev => {
			return prev.filter(item => getUniqueIdForSuggestion(item) !== getUniqueIdForSuggestion(suggestion));
		});
		setPendingItems(prev => {
			const newItems = [...prev, suggestion];
			return newItems;
		});
	};

	const processItem = React.useCallback(
		async (suggestion: IContentCalendarSelectedSuggestion) => {
			const result = await aiAssistantCreateConversationMutation.mutateAsync({
				assistantName: 'SiteWideAgent',
				impersonationContext,
			});

			const customContentForSuggestion = customContent?.[getUniqueIdForSuggestion(suggestion)];
			console.log(customContent);

			const action: any = {
				_type: 'ContentGenerationAction',
				contentType,
			} as any;

			if (customContentForSuggestion) {
				action.content = customContentForSuggestion.content;
			} else {
				action.contentId = suggestion.templateReference.templateId;
			}

			aiPromptMutation.mutate({
				assistantName: 'SiteWideAgent',
				conversationId: result.id,
				suggestion,
				chatRequest: {
					_type: 'AIActionRequest',
					action,
				},
			});
		},
		[aiAssistantCreateConversationMutation, aiPromptMutation, impersonationContext, contentType, customContent]
	);

	const handleAiGenerationComplete = React.useCallback(
		(suggestion: IContentCalendarSelectedSuggestion, toolCall: TToolCall) => {
			const nextItems = pendingItems.filter(
				item => getUniqueIdForSuggestion(suggestion) !== getUniqueIdForSuggestion(item)
			);

			onAiGenerationComplete(suggestion, toolCall);
			setPendingItems(nextItems);
			if (nextItems.length === 0) {
				setStartProcessing(false);
			}
		},
		[onAiGenerationComplete, pendingItems]
	);

	const processQueue = () => {
		setStartProcessing(true);
	};

	useEffect(() => {
		if (startProcessing && pendingItems?.length) {
			processItem(pendingItems[0]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startProcessing, pendingItems]);

	useEffect(() => {
		if (pendingItems.length === 0) {
			setStartProcessing(false);
		}
	}, [pendingItems]);

	const clearQueue = React.useCallback(() => {
		setPendingItems([]);
		setStartProcessing(false);
	}, []);

	return {
		pendingItems,
		queueItemForAiGeneration,
		isGenerating,
		processQueue,
		clearQueue,
		suggestionsWithErrors,
	};
};
